import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Loader2 } from "lucide-react";

import { useLanguage } from "../i18n/LanguageContext";
import { trackHealthSectionSaved, trackHealthSectionShown } from "../lib/analytics";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  EXTENDED_SECTIONS,
  SLEEP_OPTIONS,
  buildLegacySave,
  buildSectionSave,
  healthSectionSaveStatus,
  sectionFormState,
  sectionIsTouched,
} from "../lib/healthSections";
import { HealthSectionFields, healthSaveErrorMessage, putHealthExtended } from "./HealthSectionForm";

/**
 * Full health snapshot on Account and the person profile.
 * Three collapsible sections; legacy-only fields live under More.
 * Per-item No replaces the global "nothing to add" checkbox.
 */
const HealthProfileExtendedForm = ({
  initialExtended = {},
  onSaved,
  submitLabel,
  subjectId = null,
  subjectKind = null,
}) => {
  const { t } = useLanguage();
  const [state, setState] = useState(() => sectionFormState(initialExtended));
  const [saving, setSaving] = useState(false);
  const extendedKey = JSON.stringify(initialExtended ?? {});
  const resolvedKind =
    subjectKind === "self" || subjectKind === "dependent"
      ? subjectKind
      : subjectId
        ? "dependent"
        : "self";

  useEffect(() => {
    setState(sectionFormState(initialExtended));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extendedKey]);

  useEffect(() => {
    EXTENDED_SECTIONS.forEach((section) => {
      trackHealthSectionShown({
        section,
        surface: "profile_page",
        subjectKind: resolvedKind,
      });
    });
  }, [resolvedKind]);

  const patch = (partial) => setState((prev) => ({ ...prev, ...partial }));

  const handleSave = async () => {
    const sections = EXTENDED_SECTIONS.filter((section) => sectionIsTouched(section, state));
    const legacy = buildLegacySave(state);
    const bodies = sections.map((section) => buildSectionSave(section, state));
    if (Object.keys(legacy).length) bodies.push(legacy);
    if (bodies.length === 0) return;
    setSaving(true);
    const failures = [];
    let last;
    try {
      // Sequential: each save merges the same profile. A failed call must not
      // skip the remaining sections or the legacy fields.
      for (const body of bodies) {
        try {
          last = await putHealthExtended(subjectId, body);
          if (body.section) {
            trackHealthSectionSaved({
              section: body.section,
              surface: "profile_page",
              subjectKind: resolvedKind,
              status: healthSectionSaveStatus(body),
            });
          }
        } catch (err) {
          failures.push(err);
        }
      }
      if (failures.length === bodies.length) {
        toast.error(healthSaveErrorMessage(failures[0], t));
        return;
      }
      if (failures.length) {
        try {
          await onSaved?.(last);
        } finally {
          toast.error(t("healthProfile.saveIncomplete"));
        }
        return;
      }
      toast.success(t("healthProfile.extendedSaved"));
      onSaved?.(last);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="health-extended-form">
      {EXTENDED_SECTIONS.map((section) => (
        <Collapsible key={section} defaultOpen className="space-y-2 border-b border-border pb-3">
          <CollapsibleTrigger
            className="flex w-full items-center justify-between text-sm font-medium"
            data-testid={`health-section-toggle-${section}`}
          >
            {t(`healthProfile.section.${section}`)}
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-2">
            <HealthSectionFields section={section} state={state} onChange={patch} />
            {section === "history" && (
              <Collapsible>
                <CollapsibleTrigger
                  className="text-xs text-primary hover:underline"
                  data-testid="health-more-history"
                >
                  {t("healthProfile.more")}
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 space-y-1.5">
                  <Label>{t("healthProfile.chronicConditionsOther")}</Label>
                  <Input
                    value={state.chronic_conditions_other}
                    onChange={(e) => patch({ chronic_conditions_other: e.target.value })}
                    data-testid="health-conditions-other"
                  />
                </CollapsibleContent>
              </Collapsible>
            )}
            {section === "lifestyle" && (
              <Collapsible>
                <CollapsibleTrigger
                  className="text-xs text-primary hover:underline"
                  data-testid="health-more-lifestyle"
                >
                  {t("healthProfile.more")}
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 space-y-3">
                  <div className="space-y-1.5">
                    <Label>{t("healthProfile.activityDays")}</Label>
                    <Input
                      type="number"
                      min={0}
                      max={7}
                      value={state.activity_days_per_week}
                      onChange={(e) => patch({ activity_days_per_week: e.target.value })}
                      data-testid="health-activity-days"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("healthProfile.sleepRegular")}</Label>
                    <Select
                      value={state.sleep_regular || undefined}
                      onValueChange={(value) => patch({ sleep_regular: value })}
                    >
                      <SelectTrigger data-testid="health-sleep-regular">
                        <SelectValue placeholder={t("healthProfile.selectPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {SLEEP_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {t(`healthProfile.sleep.${opt}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("healthProfile.notesOther")}</Label>
                    <Textarea
                      value={state.notes_other}
                      onChange={(e) => patch({ notes_other: e.target.value })}
                      className="min-h-[60px]"
                      data-testid="health-notes"
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </CollapsibleContent>
        </Collapsible>
      ))}

      <Button
        type="button"
        onClick={handleSave}
        disabled={saving}
        data-testid="health-extended-save"
      >
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          submitLabel || t("healthProfile.saveAndContinue")
        )}
      </Button>
    </div>
  );
};

export default HealthProfileExtendedForm;
